import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Modal, Form } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { decode, encode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { EyeIcon } from 'assets/icons';
import { Dropdown } from 'components/Forms';
import { DateTimePicker, IconTooltip, TextArea } from 'components/UI';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { buildQuestionnaireOptions } from 'utils/Questionnaires';

import GenerateActionEngine from '../../GenerateActionEngine';
import RecurrenceEngine from '../../RecurrenceEngine';
import PreviewQuestionnaireModal from './PreviewQuestionnaireModal';

interface IAddObservationsModalProps {
  show: boolean;
  handleClose: any;
  handleAdd: any;
  questionnairesData: ISurvey[];
  carePlanTemplate?: ICarePlanTemplate;
  questionnaire?: ICarePlanQuestionnaire;
}

const AddEditQuestionnaireModal = ({
  show,
  handleClose,
  handleAdd,
  questionnairesData,
  carePlanTemplate,
  questionnaire
}: IAddObservationsModalProps) => {
  const intl = useIntl();
  const patientUid = useAppSelector(({ patient }) => patient.data?.uid);
  const locale = useAppSelector((state) => state.intl.locale);

  const [questionnaireSelected, setQuestionnaireSelected] = useState(questionnaire?.title || '');
  const [notificationContent, setNotificationContent] = useState(
    questionnaire?.action.survey?.messageText ||
      intl.formatMessage({ id: 'timeToTakeQuestionnaire' })
  );

  const now = DateTime.now().setLocale(locale);
  const questionnaireDate =
    questionnaire?.send_time && typeof questionnaire?.send_time !== 'number'
      ? DateTime.fromFormat(questionnaire.send_time, 'yyyy-MM-dd HH:mm:ss')
      : undefined;
  const [date, setDate] = useState<DateTime>(questionnaireDate || now.plus({ hours: 1 }));

  const [actionsOnTask, setActionsOnTask] = useState<ICarePlanActionOnTask[]>(
    questionnaire?.action.survey?.actions_on_task || []
  );
  const [recurrence, setRecurrence] = useState(
    questionnaire?.isRepeating ? questionnaire.recurrence : ''
  );
  const [showPreview, setShowPreview] = useState(false);
  // XTC-3999 - disable unscheduled questionnaires
  // const [switchScheduledEvent, setSwitchScheduledEvent] = useState(
  //   questionnaire?.isScheduled ? 'yes' : 'no'
  // );
  const [validRecurrence, setValidRecurrence] = useState(true);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);

  const modalTitle = questionnaire
    ? `${intl.formatMessage({ id: 'editKey' })} ${intl.formatMessage({
        id: 'questionnaireKey'
      })}`
    : intl.formatMessage({ id: 'addQuestionnaireKey' });

  const submitButtonLabel = questionnaire ? 'saveButtonTitle' : 'add';

  const [{ loading, error: postError }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patientUid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const _handleAdd = async () => {
    const _questionnaire = questionnairesData.find((e) => e.title === questionnaireSelected);
    // XTC-3999 - disable unscheduled questionnaires
    // const scheduled = switchScheduledEvent === 'yes';
    if (!_questionnaire) return;

    const questionnaireToCreate = {
      edit_status: questionnaire ? 'updated' : 'new',
      id: questionnaire?.id,
      action: {
        survey: {
          messageText: notificationContent,
          linkText: _questionnaire.title,
          surveyId: [_questionnaire.survey_id],
          // XTC-3999 - disable unscheduled questionnaires
          // actions_on_task: scheduled ? actionsOnTask : [],
          actions_on_task: actionsOnTask
        },
        localDateTime: date.toFormat('yyyy-MM-dd HH:mm')
        // XTC-3999 - disable unscheduled questionnaires
        // localDateTime: scheduled ? date.toFormat('yyyy-MM-dd HH:mm') : '',
      },
      // XTC-3999 - disable unscheduled questionnaires
      // recurrence: scheduled ? recurrence : '',
      recurrence: recurrence
    };

    await post({
      data: {
        scheduled: [questionnaireToCreate],
        carePlanTemplate
      }
    });

    handleAdd();
  };

  const _handleClose = () => {
    handleClose(false);
  };

  const onAddActions = (generatedActions: ICarePlanActionOnTask[]) => {
    setActionsOnTask(generatedActions);
  };

  const onChangeRecurrence = (_recurrence: string, _valid: boolean) => {
    setRecurrence(_recurrence);
    setValidRecurrence(_valid);
  };

  const handleShowPreview = () => {
    setShowPreview(!showPreview);
  };

  const questionnaireDataSelected = questionnairesData.find(
    (elem) => elem.title === questionnaireSelected
  );

  useEffect(() => {
    const isQuestionnaireIdentical =
      questionnaireSelected === questionnaire?.title &&
      date.toMillis() === questionnaireDate?.toMillis() &&
      recurrence === (questionnaire?.isRepeating ? questionnaire.recurrence : '');
    setAddButtonDisabled(
      // XTC-3999 - disable unscheduled questionnaires
      // !questionnaireSelected || (!validRecurrence && switchScheduledEvent === 'yes')
      !questionnaireSelected || !validRecurrence || isQuestionnaireIdentical
    );
    // }, [questionnaireSelected, validRecurrence, switchScheduledEvent]);
  }, [questionnaireSelected, validRecurrence, recurrence, questionnaire, date, questionnaireDate]);
  return (
    <Modal
      show={show}
      onHide={_handleClose}
      backdrop="static"
      className={showPreview ? 'sub-modal' : ''}
      data-test="add-edit-questionnaire-modal"
    >
      <Modal.Header className="align-items-center" closeButton>
        <Modal.Title data-test="add-edit-questionnaire-title">{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <Form.Label>{intl.formatMessage({ id: 'questionnaireKey' })}</Form.Label>
          <div className="d-flex align-items-center">
            <Dropdown
              dataTestId="add-edit-questionnaire-select"
              variant="form"
              className="w-400"
              options={buildQuestionnaireOptions(questionnairesData)}
              value={buildQuestionnaireOptions(questionnairesData).find(
                (elem) => elem.value === questionnaireSelected
              )}
              onChange={({ value }: ISelectOption) => setQuestionnaireSelected(value)}
            />
            <Button
              data-test="add-edit-questionnaire-preview-btn"
              variant="link"
              onClick={handleShowPreview}
              className="m-2"
              disabled={!questionnaireDataSelected}
            >
              <IconTooltip
                dataTestId="add-edit-questionnaire-preview-icon"
                overlay={intl.formatMessage({ id: 'previewKey' })}
              >
                <EyeIcon className="icon align-middle" />
              </IconTooltip>
            </Button>
          </div>
          {questionnaireDataSelected && (
            <PreviewQuestionnaireModal
              show={showPreview}
              questionnaire={questionnaireDataSelected}
              handleClose={() => setShowPreview(false)}
            />
          )}
        </div>

        <TextArea
          dataTestId="add-edit-questionnaire-message"
          value={decode(notificationContent)}
          onChange={(e) => setNotificationContent(encode(e.target.value))}
          label={intl.formatMessage({ id: 'messageKey' })}
          rows={3}
          containerClassName="mt-4"
        />

        {/* XTC-3999 - disable unscheduled questionnaires */}
        {/* <div
          className="mb-2 mt-4 d-flex align-items-center"
          data-test="add-edit-questionnaire-scheduled"
        >
          <Form.Label className="m-0">
            {intl.formatMessage({ id: // 'scheduledQuestionnaireKey' })}
          </Form.Label>
          <ToggleButtonGroup
            type="checkbox"
            size="sm"
            className="ms-2 z-0"
            value={[switchScheduledEvent]}
          >
            <ToggleButton
              data-test="add-edit-questionnaire-scheduled-yes"
              value="yes"
              variant="outline-primary"
              onClick={() => setSwitchScheduledEvent('yes')}
            >
              {intl.formatMessage({ id: 'yesKey' })}
            </ToggleButton>
            <ToggleButton
              data-test="add-edit-questionnaire-scheduled-no"
              value="no"
              variant="outline-primary"
              onClick={() => setSwitchScheduledEvent('no')}
            >
              {intl.formatMessage({ id: 'noKey' })}
            </ToggleButton>
          </ToggleButtonGroup>
        </div> */}
        {/* {switchScheduledEvent === 'yes' && (
          <> */}
        <div className="d-flex flex-column mt-4">
          <DateTimePicker
            dataTestPrefix="add-edit-questionnaire"
            date={date}
            todayLimit="min"
            onChangeDate={setDate}
            minDate={now}
          />
        </div>

        <RecurrenceEngine
          recurrence={recurrence}
          onChange={onChangeRecurrence}
          isActive={questionnaire?.isRepeating || false}
          date={date}
        />
        <GenerateActionEngine onAddActions={onAddActions} currentActions={actionsOnTask} />
        {/* </>
        )} */}

        {loading && <Loading className="mt-4" />}
        {postError && <Error className="mt-4" retry={_handleAdd} dismissible={false} />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          onClick={_handleClose}
          data-test="add-edit-questionnaire-modal-close-btn"
        >
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
        <Button
          data-test="add-edit-questionnaire-modal-save-btn"
          variant="primary"
          onClick={_handleAdd}
          disabled={addButtonDisabled}
        >
          {intl.formatMessage({ id: submitButtonLabel })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditQuestionnaireModal;
